import React from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { useState, useRef, useEffect } from 'react';
import styles from '../../subject.module.css';
import Button from 'components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEllipsisV, faEdit } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from 'hooks/auth';
import { UserRole } from 'types/auth';
import { subjectApi } from '../../api/subjectApi';
import { moduleApi } from '../../api/moduleApi';
import { Subject, Module } from '../../types/subject';
import Loading from 'components/Loading/Loading';
import SearchInput from 'components/SearchInput/SearchInput';
import ModuleCard from '../ModuleCard/ModuleCard';

const ModuleList: React.FC = () => {
    const { subjectSlug } = useParams<{ subjectSlug: string }>();
    const navigate = useNavigate();
    const { user } = useAuth()!;
    const isStudent = user?.role === UserRole.STUDENT;
    const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

    const [subject, setSubject] = useState<Subject | null>(null);
    const [modules, setModules] = useState<Module[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [openModuleMenuId, setOpenModuleMenuId] = useState<string | null>(null);
    const moduleMenuRef = useRef<HTMLDivElement>(null);

    const fetchData = async () => {
        if (!subjectSlug) return;
        setLoading(true);
        try {
            const subjectRes = await subjectApi.getBySlug(subjectSlug);
            const subjectData = subjectRes.data;
            setSubject(subjectData);

            const modulesRes = await moduleApi.getAll(subjectData.id);
            setModules(modulesRes.data);
        } catch (error) {
            console.error('Error fetching module list data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [subjectSlug]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (moduleMenuRef.current && !moduleMenuRef.current.contains(event.target as Node)) {
                setOpenModuleMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Mock module progress data - in a real app this would come from an API
    const getModuleProgress = (moduleId: string) => {
        const mockData: Record<string, { best: number; improvement: number; attempts: number; mastered: boolean }> = {
            'ste17005': { best: 90, improvement: 80, attempts: 2, mastered: true },
            'algebra': { best: 85, improvement: 75, attempts: 3, mastered: true },
            'calculus': { best: 60, improvement: 84, attempts: 1, mastered: false },
            'mechanics': { best: 70, improvement: 65, attempts: 2, mastered: false },
            'genetics': { best: 55, improvement: 50, attempts: 1, mastered: false },
        };
        return mockData[moduleId] || { best: 0, improvement: 0, attempts: 0, mastered: false };
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <Loading message="Loading modules..." />
            </div>
        );
    }

    if (!subject) {
        return (
            <div className={styles.container}>
                <h1 className={styles.title}>Subject not found</h1>
            </div>
        );
    }

    const filteredModules = modules.filter(module => 
        module.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        module.slug.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className={styles.container}>
            <header className={styles.pageHeader}>
                <div className={styles.titleSection}>
                    <h1 className={styles.title} style={{ marginBottom: 0 }}>{subject.name} Module</h1>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <SearchInput 
                        value={searchQuery} 
                        onChange={setSearchQuery} 
                        placeholder="Search modules..." 
                    />
                    {isSuperAdmin && (
                        <Button variant="primary" onClick={() => navigate(`/subject/${subjectSlug}/add-module`)}>
                            + Add Module
                        </Button>
                    )}
                </div>
            </header>

            {isStudent && (() => {
                const attemptedCount = modules.reduce((acc, m) => getModuleProgress(m.id).attempts > 0 ? acc + 1 : acc, 0);
                const scores = modules
                    .map(m => getModuleProgress(m.id))
                    .filter(p => p.attempts > 0)
                    .map(p => p.best);
                const avg = scores.length > 0
                    ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
                    : 0;

                return (
                    <div className={styles.moduleHeaderStats}>
                        <div className={styles.attemptedModules}>
                            {attemptedCount} / {modules.length} Modules Attempted
                        </div>
                        <div className={styles.avgScoreLine}>
                            Avg Score: <b>{avg}%</b>
                        </div>
                    </div>
                );
            })()}

            <div className={styles.grid}>
                {filteredModules.map((module) => (
                    <ModuleCard
                        key={module.id}
                        module={module}
                        subjectSlug={subject.slug}
                        isStudent={isStudent}
                        isSuperAdmin={isSuperAdmin}
                        progress={getModuleProgress(module.id)}
                        openModuleMenuId={openModuleMenuId}
                        setOpenModuleMenuId={setOpenModuleMenuId}
                        moduleMenuRef={moduleMenuRef}
                    />
                ))}
            </div>
        </div>
    );
};

export default ModuleList;
